function tieredSavingsState(cart) {
  const subtotal = cart.items.reduce((sum, item) => sum + item.final_line_price, 0);
  const eligibleSubtotal = cart.items.reduce((sum, item) => sum + (item.gift_card ? 0 : item.final_line_price), 0);
  const tier = eligibleSubtotal >= 20000 ? 'highest' : eligibleSubtotal >= 10000 ? 'second' : 'first';
  const remaining = tier === 'highest' ? 0 : (tier === 'second' ? 20000 : 10000) - eligibleSubtotal;
  return { subtotal, tier, remaining };
}

function cartDiscountRows(cart, subtotal) {
  const difference = subtotal - cart.total_price;
  if (difference <= 0) return [];
  const rows = (cart.cart_level_discount_applications || []).map((entry) => {
    const application = entry.discount_application || entry;
    return {
      title: application.title,
      amount: Number.isFinite(entry.amount) ? entry.amount : application.total_allocated_amount
    };
  }).filter((row) => Number.isFinite(row.amount) && row.amount > 0);
  if (rows.reduce((sum, row) => sum + row.amount, 0) === difference) return rows;
  return [{ title: null, amount: difference }];
}

document.addEventListener('DOMContentLoaded', () => {
  const root = document.documentElement;
  let lastTrigger = null;

  const specularSelector = [
    'a.button',
    'a.product-card__quick',
    'a.product-card__view',
    '.collection-products .product-card',
    '.product-recommendations .product-card',
    '.search-results .product-card'
  ].join(',');
  const decorateSpecularControls = (scope = document) => {
    const controls = [];
    if (scope.nodeType === 1 && scope.matches?.(specularSelector)) controls.push(scope);
    scope.querySelectorAll?.(specularSelector).forEach((control) => controls.push(control));
    controls.forEach((control) => {
      control.classList.add('specular-control');
      if (control.matches('.button--primary')) {
        control.classList.add('specular-control--primary');
      }
    });
  };

  decorateSpecularControls();
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) decorateSpecularControls(node);
    }));
  }).observe(document.body, { childList: true, subtree: true });

  if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.addEventListener('pointermove', (event) => {
      const control = event.target.closest?.('.specular-control');
      if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return;
      const rect = control.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 100;
      const y = ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 100;
      control.style.setProperty('--specular-x', `${Math.max(0, Math.min(100, x)).toFixed(1)}%`);
      control.style.setProperty('--specular-y', `${Math.max(0, Math.min(100, y)).toFixed(1)}%`);
    }, { passive: true });
    document.addEventListener('pointerout', (event) => {
      const control = event.target.closest?.('.specular-control');
      if (!control || control.contains(event.relatedTarget)) return;
      control.style.setProperty('--specular-x', '50%');
      control.style.setProperty('--specular-y', '50%');
    }, { passive: true });
  }

  const closeCardNav = (returnFocus = false) => {
    document.querySelectorAll('[data-card-nav]').forEach((shell) => {
      const nav = shell.querySelector('.card-nav');
      const toggle = shell.querySelector('[data-card-nav-toggle]');
      const panel = shell.querySelector('[data-card-nav-panel]');
      nav?.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.setAttribute('aria-label', toggle.dataset.openLabel || 'Open navigation menu');
      panel?.setAttribute('aria-hidden', 'true');
      if (returnFocus) toggle?.focus();
    });
  };

  document.querySelectorAll('[data-card-nav]').forEach((shell) => {
    const nav = shell.querySelector('.card-nav');
    const toggle = shell.querySelector('[data-card-nav-toggle]');
    const panel = shell.querySelector('[data-card-nav-panel]');
    toggle?.addEventListener('click', () => {
      const open = !nav.classList.contains('is-open');
      closeCardNav();
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? (toggle.dataset.closeLabel || 'Close navigation menu') : (toggle.dataset.openLabel || 'Open navigation menu'));
      panel?.setAttribute('aria-hidden', open ? 'false' : 'true');
    });
  });

  const closeDrawers = () => {
    closeCardNav();
    document.querySelectorAll('[data-drawer]').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    root.classList.remove('drawer-open');
    if (lastTrigger) lastTrigger.focus();
    lastTrigger = null;
  };

  document.querySelectorAll('[data-drawer-open]').forEach((button) => {
    button.addEventListener('click', () => {
      closeDrawers();
      const drawer = document.querySelector(`[data-drawer="${button.dataset.drawerOpen}"]`);
      if (!drawer) return;
      lastTrigger = button;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      root.classList.add('drawer-open');
      drawer.querySelector('input, button, a')?.focus();
    });
  });

  document.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', closeDrawers));
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') { closeCardNav(true); closeDrawers(); } });
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-card-nav]')) closeCardNav();
  });

  document.querySelectorAll('[data-submenu-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const item = button.closest('.drawer-menu__item');
      const open = item?.classList.toggle('is-open');
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  document.querySelectorAll('[data-announcement]').forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('[data-announcement-item]'));
    if (items.length < 2) return;
    let index = 0;
    const show = (next) => {
      items[index].classList.remove('is-active');
      index = (next + items.length) % items.length;
      items[index].classList.add('is-active');
    };
    bar.querySelector('[data-announcement-prev]')?.addEventListener('click', () => show(index - 1));
    bar.querySelector('[data-announcement-next]')?.addEventListener('click', () => show(index + 1));
  });

  document.querySelectorAll('[data-masked-heading]').forEach((heading) => {
    const glyph = heading.querySelector('[data-masked-heading-glyph]');
    const media = heading.querySelector('[data-masked-heading-media]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const fitGlyph = () => {
      if (!glyph?.getComputedTextLength) return;
      glyph.style.fontSize = '176px';
      const length = glyph.getComputedTextLength();
      if (length > 1240) glyph.style.fontSize = `${Math.max(72, 176 * (1240 / length))}px`;
    };

    fitGlyph();
    if (document.fonts?.ready) document.fonts.ready.then(fitGlyph).catch(() => {});

    if (reduceMotion) {
      heading.classList.add('is-visible');
    } else {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          heading.classList.add('is-visible');
          observer.disconnect();
        }
      }, { threshold: 0.25 });
      observer.observe(heading);

      heading.addEventListener('pointermove', (event) => {
        const rect = heading.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / Math.max(rect.width, 1) - 0.5) * -26;
        const y = ((event.clientY - rect.top) / Math.max(rect.height, 1) - 0.5) * -14;
        media?.style.setProperty('--mask-x', `${x.toFixed(1)}px`);
        media?.style.setProperty('--mask-y', `${y.toFixed(1)}px`);
      });
      heading.addEventListener('pointerleave', () => {
        media?.style.setProperty('--mask-x', '0px');
        media?.style.setProperty('--mask-y', '0px');
      });
    }
  });

  document.querySelectorAll('[data-cinematic-collection]').forEach((collection) => {
    const shelf = collection.querySelector('[data-cinematic-shelf]');
    const cards = Array.from(collection.querySelectorAll('.collection-cinema-card--shelf'));
    const current = collection.querySelector('[data-cinematic-current]');
    const progress = collection.querySelector('[data-cinematic-progress]');
    const total = Math.max(1, Number(collection.dataset.collectionTotal || cards.length));
    if (!shelf || !cards.length || !current || !progress) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const shelfCenter = shelf.scrollLeft + (shelf.clientWidth / 2);
      let active = 0;
      let distance = Number.POSITIVE_INFINITY;
      cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
        const nextDistance = Math.abs(cardCenter - shelfCenter);
        if (nextDistance < distance) {
          distance = nextDistance;
          active = index;
        }
      });
      current.textContent = String(active + 1).padStart(2, '0');
    progress.style.setProperty('--cinematic-progress-scale', String(Math.max(.08, (active + 1) / total)));
    };
    shelf.addEventListener('scroll', () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });

  document.querySelectorAll('[data-quantity]').forEach((quantity) => {
    const input = quantity.querySelector('input');
    const update = (value) => {
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    quantity.querySelector('[data-quantity-minus]')?.addEventListener('click', () => { update(Math.max(Number(input.min || 1), Number(input.value || 1) - 1)); });
    quantity.querySelector('[data-quantity-plus]')?.addEventListener('click', () => { update(Number(input.value || 1) + 1); });
  });

  document.querySelectorAll('[data-cart-preview-remove]').forEach((button) => {
    button.addEventListener('click', () => {
      const scope = button.closest('.cart-layout,.drawer-cart-items');
      button.closest('.cart-item,.drawer-cart-item')?.remove();
      scope?.querySelectorAll('[data-preview-cart-total]').forEach((total) => { total.textContent = formatMoney(0); });
    });
  });

  const supportProduct = document.querySelector('[data-support-product]');
  const requestedProduct = new URLSearchParams(window.location.search).get('product');
  if (supportProduct && requestedProduct && !supportProduct.value) supportProduct.value = requestedProduct;

  document.querySelectorAll('[data-installation-guide-open]').forEach((button) => {
    const dialog = document.getElementById(button.dataset.installationGuideOpen);
    if (!dialog) return;
    button.addEventListener('click', () => dialog.showModal());
  });

  document.querySelectorAll('[data-installation-guide-dialog]').forEach((dialog) => {
    const close = () => {
      dialog.querySelector('video')?.pause();
      dialog.close();
    };
    dialog.querySelector('[data-installation-guide-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => dialog.querySelector('video')?.pause());
  });

  const formatMoney = (cents, checkout = false) => {
    if (!checkout && root.dataset.referenceCurrency === 'GBP') {
      return root.dataset.referencePriceFormat.replace('__AMOUNT__', String(Math.round(cents / 120)));
    }
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const formatted = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
    return !checkout && root.dataset.showCurrencyCode === 'true' ? `${formatted} ${currency}` : formatted;
  };

  document.querySelectorAll('[data-cart-auto-update]').forEach((control) => {
    const input = control.querySelector('input');
    input?.addEventListener('change', async () => {
      const item = control.closest('.drawer-cart-item');
      const previous = Number(input.dataset.syncedValue || input.defaultValue || 1);
      const quantity = Math.max(0, Number(input.value || 0));
      input.value = quantity;
      const errorMessage = document.querySelector('[data-cart-update-error]');
      if (errorMessage) errorMessage.hidden = true;
      control.setAttribute('aria-busy', 'true');
      document.querySelectorAll('[data-cart-auto-update] button, [data-cart-auto-update] input').forEach((field) => { field.disabled = true; });
      try {
        const response = await fetch(control.dataset.cartChangeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(control.dataset.cartLineIndex), quantity })
        });
        if (!response.ok) throw new Error('Cart update failed');
        const cart = await response.json();
        if (document.querySelector('.cart-page') || cart.item_count === 0) {
          window.location.reload();
          return;
        }
        if (quantity === 0) item?.remove();
        document.querySelectorAll('.drawer-cart-item').forEach((row, index) => {
          const cartItem = cart.items[index];
          const rowControl = row.querySelector('[data-cart-auto-update]');
          const rowInput = rowControl?.querySelector('input');
          if (!cartItem || !rowControl || !rowInput) return;
          rowControl.dataset.cartLineIndex = String(index + 1);
          rowControl.dataset.cartLineKey = cartItem.key;
          rowInput.value = String(cartItem.quantity);
          rowInput.dataset.syncedValue = String(cartItem.quantity);
          const remove = row.querySelector('.drawer-cart-item__remove');
          if (remove) remove.href = `${rowControl.dataset.cartChangeUrl.replace(/\.js$/, '')}?line=${index + 1}&quantity=0`;
          const lineTotal = row.querySelector('[data-cart-line-total]');
          if (lineTotal) lineTotal.textContent = formatMoney(cartItem.final_line_price);
        });
        const { subtotal, tier, remaining } = tieredSavingsState(cart);
        document.querySelectorAll('[data-cart-subtotal]').forEach((amount) => { amount.textContent = formatMoney(subtotal); });
        document.querySelectorAll('[data-cart-total]').forEach((total) => { total.textContent = formatMoney(cart.total_price); });
        document.querySelectorAll('.drawer-cart-items [data-cart-usd-total]').forEach((amount) => { amount.textContent = formatMoney(cart.total_price, true) + ' USD'; });
        document.querySelectorAll('[data-tiered-savings]').forEach((promotion) => {
          const status = promotion.querySelector('[data-tiered-status]');
          if (!status) return;
          const template = tier === 'highest' ? promotion.dataset.statusHighest : tier === 'second' ? promotion.dataset.statusToSecond : promotion.dataset.statusToFirst;
          status.textContent = template.replace('__AMOUNT__', new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(remaining / 100));
        });
        document.querySelectorAll('[data-cart-discounts]').forEach((list) => {
          list.replaceChildren();
          cartDiscountRows(cart, subtotal).forEach((discount) => {
            const row = document.createElement('div');
            row.className = 'drawer-cart-discount';
            const name = document.createElement('span');
            name.textContent = discount.title || list.dataset.discountLabel;
            const value = document.createElement('strong');
            value.textContent = `-${formatMoney(discount.amount)}`;
            row.append(name, value);
            list.append(row);
          });
        });
        document.querySelectorAll('[data-cart-count]').forEach((count) => {
          count.textContent = String(cart.item_count);
          count.setAttribute('aria-label', `${cart.item_count} items`);
        });
        if (cart.item_count === 0) document.querySelectorAll('.drawer-cart-items [name="checkout"]').forEach((checkout) => { checkout.disabled = true; });
      } catch (error) {
        input.value = previous;
        if (errorMessage) errorMessage.hidden = false;
      } finally {
        control.removeAttribute('aria-busy');
        document.querySelectorAll('[data-cart-auto-update] button, [data-cart-auto-update] input').forEach((field) => { field.disabled = false; });
      }
    });
  });

  const updateCartAction = window.Shopify?.actions?.updateCart;
  if (typeof updateCartAction?.configure === 'function') {
    updateCartAction.configure({
      eventTarget: (meta) => meta.action === 'add'
        ? document.querySelector('[data-product-form]')
        : document,
      async handler(defaultHandler, payload, options) {
        const result = await defaultHandler();
        if (!result.userErrors?.length && options?.event?.context === 'product') window.location.assign('/cart');
        return result;
      }
    });
  }

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const rootEl = form.closest('[data-product-root]');
    const variantsNode = form.querySelector('[data-product-variants]');
    const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
    const idInput = form.querySelector('[data-variant-id]');
    const addButton = form.querySelector('[data-add-to-cart]');
    const price = rootEl?.querySelector('[data-product-price]');
    const comparePrice = rootEl?.querySelector('[data-compare-price]');
    const sku = rootEl?.querySelector('[data-product-sku]');
    const skuLine = rootEl?.querySelector('[data-sku-line]');
    const vehicleGroup = form.querySelector('[data-vehicle-options]');
    const vehicleRadios = vehicleGroup?.querySelectorAll('input[type="radio"]') || [];
    const vehiclePrompt = form.querySelector('[data-vehicle-prompt]');
    const payment = form.querySelector('[data-product-payment]');
    const optionGroups = Array.from(form.querySelectorAll('.variant-group'));
    const requestedVariantId = new URLSearchParams(window.location.search).get('variant');
    const requestedVariant = variants.find((variant) => String(variant.id) === requestedVariantId);
    const availability = rootEl?.querySelector('[data-product-availability]');
    const availableText = availability?.dataset.availableLabel || 'IN STOCK';
    const soldOutText = addButton?.dataset.soldLabel || availability?.dataset.soldLabel || 'SOLD OUT';
    const addText = addButton?.dataset.addLabel || 'ADD TO CART';
    const selectionText = addButton?.dataset.selectionLabel;
    const unavailableText = addButton?.dataset.unavailableLabel;

    vehicleRadios.forEach((radio) => {
      radio.disabled = false;
      if (!requestedVariant) radio.checked = false;
    });
    if (requestedVariant) {
      optionGroups.forEach((group, index) => {
        const select = group.querySelector('select');
        if (select) select.value = requestedVariant.options[index];
        group.querySelectorAll('input[type="radio"]').forEach((radio) => { radio.checked = radio.value === requestedVariant.options[index]; });
      });
    }

    const showVariantMedia = (variant) => {
      if (!variant?.featured_media?.id) return;
      const gallery = rootEl?.querySelector('[data-product-gallery]');
      if (typeof gallery?.productCarouselGoToMedia === 'function') {
        gallery.productCarouselGoToMedia(variant.featured_media.id);
        return;
      }
      const media = rootEl?.querySelector(`[data-product-media="${variant.featured_media.id}"]`);
      if (!media) return;
      media.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      rootEl.querySelectorAll('[data-media-target]').forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaTarget === String(variant.featured_media.id)));
    };

    const updateVariant = (updateUrl = true) => {
      const selected = optionGroups.map((group) => group.querySelector('select')?.value ?? group.querySelector('input:checked')?.value);
      const needsVehicle = vehicleGroup && !vehicleGroup.querySelector('input:checked');
      const variant = needsVehicle ? null : optionGroups.length
        ? variants.find((item) => item.options.every((option, index) => option === selected[index]))
        : variants.find((item) => String(item.id) === idInput?.value) || variants[0];
      if (idInput) {
        idInput.value = variant ? String(variant.id) : '';
        idInput.disabled = !variant?.available;
      }
      if (vehiclePrompt) vehiclePrompt.hidden = !needsVehicle;
      if (payment) {
        payment.hidden = !variant?.available;
        payment.inert = !variant?.available;
      }
      if (availability) availability.hidden = !variant;
      if (skuLine) skuLine.hidden = !variant?.sku?.trim();
      if (!variant) {
        if (sku) sku.textContent = '';
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = needsVehicle ? selectionText : unavailableText;
        }
        return;
      }
      if (price) price.textContent = formatMoney(variant.price);
      rootEl?.querySelectorAll('[data-product-usd-price]').forEach((amount) => { amount.textContent = formatMoney(variant.price, true) + ' USD'; });
      if (comparePrice) {
        comparePrice.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
      }
      if (sku) sku.textContent = variant.sku || '';
      if (availability) {
        availability.textContent = variant.available ? availableText : soldOutText;
        availability.classList.toggle('is-available', variant.available);
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? addText : soldOutText;
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        showVariantMedia(variant);
      }
    };

    form.querySelectorAll('.variant-group input[type="radio"], .variant-group select').forEach((control) => control.addEventListener('change', () => {
      const legendValue = control.closest('fieldset')?.querySelector('[data-option-value]');
      if (legendValue) legendValue.textContent = control.value;
      updateVariant();
    }));
    updateVariant(false);

    form.addEventListener('submit', (event) => {
      if (!idInput?.disabled && variants.some((variant) => String(variant.id) === idInput.value && variant.available)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      vehicleRadios[0]?.focus();
      vehicleRadios[0]?.reportValidity();
    }, true);

    if (typeof window.fetch === 'function') {
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (form.dataset.submitting === 'true') return;

        const errorMessage = form.querySelector('[data-product-form-error]');
        const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value || 1));
        form.dataset.submitting = 'true';
        if (addButton) {
          addButton.disabled = true;
          addButton.setAttribute('aria-busy', 'true');
          addButton.textContent = 'ADDING…';
        }
        if (errorMessage) {
          errorMessage.hidden = true;
          errorMessage.textContent = '';
        }

        try {
          const formData = new FormData(form);
          formData.set('quantity', String(quantity));
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData
          });
          const result = await response.json();
          if (!response.ok) throw Object.assign(new Error(result.description || result.message || 'This item could not be added. Please try again.'), { status: response.status });
          window.location.assign(`${window.Shopify.routes.root}cart`);
        } catch (error) {
          if (errorMessage) {
            errorMessage.textContent = error.message || 'This item could not be added. Please try again.';
            errorMessage.hidden = false;
            if (error.status === 422) {
              const cartLink = document.createElement('a');
              cartLink.href = `${window.Shopify.routes.root}cart`;
              cartLink.textContent = 'View cart';
              errorMessage.append(' Your cart may contain the available quantity. ', cartLink);
            }
          }
          if (error.status === 422) {
            // Cart Ajax can add the available stock before returning 422.
            try {
              const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
                headers: { Accept: 'application/json' }, cache: 'no-store'
              });
              if (cartResponse.ok) {
                const cart = await cartResponse.json();
                document.querySelectorAll('[data-cart-count]').forEach((count) => {
                  count.textContent = String(cart.item_count);
                  count.setAttribute('aria-label', `${cart.item_count} items`);
                });
              }
            } catch (cartError) {
              // Keep the original error and review link if the cart read fails.
            }
          }
          delete form.dataset.submitting;
          if (addButton) {
            addButton.removeAttribute('aria-busy');
            updateVariant(false);
          }
        }
      });
    }
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const media = Array.from(gallery.querySelectorAll('[data-product-media]'));
    const dots = Array.from(gallery.querySelectorAll('[data-media-target]'));
    const stage = gallery.querySelector('[data-product-carousel]');
    if (!stage || media.length === 0) return;

    const count = media.length;
    let active = 0;
    let drag = null;

    const updateSelection = (index) => {
      active = ((index % count) + count) % count;
      media.forEach((item, itemIndex) => {
        item.classList.toggle('is-active', itemIndex === active);
        item.setAttribute('aria-hidden', itemIndex === active ? 'false' : 'true');
        item.setAttribute('aria-label', `${itemIndex + 1} of ${count}`);
      });
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === active);
        dot.setAttribute('aria-current', dotIndex === active ? 'true' : 'false');
      });
    };

    const goTo = (rawIndex) => {
      const index = ((rawIndex % count) + count) % count;
      updateSelection(index);
    };

    gallery.productCarouselGoToMedia = (mediaId) => {
      const index = media.findIndex((item) => item.dataset.productMedia === String(mediaId));
      if (index >= 0) goTo(index);
    };

    dots.forEach((button, index) => button.addEventListener('click', () => goTo(index)));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => goTo(active - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => goTo(active + 1));
    stage.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(active - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(active + 1); }
    });
    stage.addEventListener('pointerdown', (event) => {
      if (count < 2) return;
      drag = { id: event.pointerId, x: event.clientX, moved: false };
    });
    stage.addEventListener('pointermove', (event) => {
      if (!drag || drag.id !== event.pointerId) return;
      const distance = event.clientX - drag.x;
      if (!drag.moved && Math.abs(distance) > 5) {
        drag.moved = true;
        stage.setPointerCapture?.(event.pointerId);
      }
      if (drag.moved) event.preventDefault();
    });
    const finishDrag = (event) => {
      if (!drag) return;
      const distance = event.clientX - drag.x;
      const shouldMove = drag.moved && Math.abs(distance) >= 44;
      drag = null;
      if (shouldMove) goTo(active + (distance < 0 ? 1 : -1));
    };
    stage.addEventListener('pointerup', finishDrag);
    stage.addEventListener('pointercancel', finishDrag);
    updateSelection(0);
  });

});
